import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { get } from 'lodash';
import { Observable, map } from 'rxjs';
import { environment } from '@environments/environment';
import { DnaSkinFaceScanProcessEvent } from '@interfaces/schema';
import { Session } from '@providers/session/session.service';

/**
 * Report Skin Health Guideline Take Picture Service.
 *
 * @exports
 * @class ReportSkinHealthGuidelineTakePictureService
 */
@Injectable()
export class ReportSkinHealthGuidelineTakePictureService {
  private readonly apollo = inject(Apollo);
  private readonly http = inject(HttpClient);
  // eslint-disable-next-line kawaī/inject-private-readonly
  protected readonly session = inject(Session);

  /**
   * Convert Base64 String to Blob.
   *
   * @param {string} base64String Base64 string to be converted.
   * @returns {Blob | null} Blob or null.
   * @memberof ReportSkinHealthGuidelineTakePictureService
   */
  base64ToBlob(base64String: string): Blob | null {
    const parts = base64String.split(',');
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/prefer-regexp-exec
    const mime = parts[0].match(/:(.*?);/)![1];
    const bstr = atob(parts[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  /**
   * Subscribe to Image Upload Process.
   *
   * @returns {Observable<DnaSkinFaceScanProcessEvent>} Observable emitting face scan process events.
   * @memberof ReportSkinHealthGuidelineTakePictureService
   */
  imageUploadProcess(): Observable<DnaSkinFaceScanProcessEvent> {
    const query = gql`
      subscription FaceUpload {
        DNASkinFaceScanProcessUpdated {
          action
          data {
            id
            pointer
            scanAttempt
            isFailGeneration
            generationStartAt
            generationTimeoutAt
            failGenerationReason
          }
        }
      }
    `;
    return this.apollo.subscribe({ query }).pipe(
      map(({ data }) => {
        return get(
          data as any,
          'DNASkinFaceScanProcessUpdated'
        ) as DnaSkinFaceScanProcessEvent;
      })
    );
  }

  /**
   * Upload Picture.
   *
   * @param {Blob} image Image file to be uploaded.
   * @returns {Observable<unknown>} Observable emitting the upload result.
   * @memberof ReportSkinHealthGuidelineTakePictureService
   */
  uploadPicture(image: Blob): Observable<unknown> {
    const operations = JSON.stringify({
      query: `
        mutation FaceScan($faceImage: Upload!) {
          DNA {
            Skin {
              FaceScan {
                attempt(faceImage: $faceImage)
              }
            }
          }
        }
      `,
      variables: { faceImage: null }
    });

    const map = JSON.stringify({ '1': ['variables.faceImage'] });

    const formData = new FormData();
    formData.append('operations', operations);
    formData.append('map', map);
    formData.append('1', image, `${Date.now().toString()}.jpeg`);

    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
      'apollo-require-preflight': 'true'
    });

    return this.http.post(environment.GRAPHQL_URI, formData, { headers });
  }
}
